import type { PlainDate } from "@bivak/domain";
import { nl } from "../../copy/nl.js";
import type { AvailabilityResponse } from "../../lib/api.js";
import { type DayCell, type DayState, monthCells, zichtbareWeken } from "../../lib/calendar.js";

const MONTH_FORMAT = new Intl.DateTimeFormat("nl-BE", { month: "long" });
const DOW = ["ma", "di", "wo", "do", "vr", "za", "zo"] as const;

/** What a hover tells a renter: the status, never the group (NFR-6). */
const STATE_LABEL: Record<DayState, string> = {
  vrij: nl.kalender.vrij,
  bezet: nl.kalender.bezet,
  wissel: nl.kalender.wisseldag,
  eigen: nl.kalender.eigenWerking,
};

function cellClass(cell: DayCell, selected: boolean): string {
  // Past days: number only, no box — the dashed borders read as noise, not
  // information (design review). Padding days don't reach here at all.
  if (cell.past) return "border-transparent bg-transparent text-mist";
  const ring = selected ? " ring-2 ring-rood ring-offset-1 ring-offset-papier" : "";
  // Free days RECEDE into the paper; taken days are the paint. Free cells as
  // bright cards drowned everything else in the beige (user feedback, same
  // pass as the admin calendar).
  switch (cell.state) {
    case "bezet":
      return `border-dennen bg-dennen text-[#e9efe8]${ring}`;
    case "wissel":
      // Departure at noon: the morning is still theirs, the rest is free.
      return `border-dennen/60 bg-[linear-gradient(135deg,var(--color-dennen)_0_46%,transparent_46%)] text-kaart${ring}`;
    case "eigen":
      return `hatch border-[#b3a887] text-[#6b675a]${ring}`;
    default:
      // No weekend tint: a fifth cell colour outside the legend read as
      // "eigen werking" to a first-time renter looking for a free weekend
      // (persona review). The bolded VR/ZA/ZO column headers carry that job.
      // Full-strength border: free cells must invite the tap, not read as a
      // static display (design review, 2026-08-21).
      return `${selected ? "border-rood bg-rood/10" : "border-lijn bg-transparent"} text-inkt${ring}`;
  }
}

/** Paging controls rendered inside the month header — where every booking app
 *  puts them, and the only place a phone user finds them again after scrolling
 *  past a month (design review, 2026-08-21). */
export type MonthNav = {
  vorige: () => void;
  volgende: () => void;
  vorigeUit: boolean;
  volgendeUit: boolean;
};

function NavKnop({
  richting,
  label,
  uit,
  onClick,
}: {
  richting: "‹" | "›";
  label: string;
  uit: boolean;
  onClick: () => void;
}) {
  // A disabled arrow looks like a broken button; an absent one says "there is
  // nothing that way". Invisible (not hidden) so the layout never shifts.
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className={`grid h-11 w-11 place-items-center rounded-lg border-2 border-lijn text-mist hover:text-inkt ${uit ? "invisible" : ""}`}
    >
      {richting}
    </button>
  );
}

export function MonthGrid({
  month,
  space,
  occupancy,
  todayIso,
  isSelected,
  onSelectDay,
  nav,
}: {
  month: PlainDate;
  space: { resourceIds: string[] };
  occupancy: AvailabilityResponse["occupancy"];
  todayIso: string;
  /** Whether a day is inside the current click-selection. */
  isSelected: (dagIso: string) => boolean;
  onSelectDay: (dagIso: string) => void;
  /** When given, ‹ › render in this month's own header. */
  nav?: MonthNav;
}) {
  const cells = zichtbareWeken(monthCells(month, space.resourceIds, occupancy, todayIso));
  const titel = `${MONTH_FORMAT.format(new Date(month.year, month.month - 1, 1))} ${month.year}`;

  return (
    // A card: the grid itself must read as an object on the paper — free days
    // recede INSIDE it, but the calendar as a whole must not (user feedback).
    <div className="rounded-2xl border border-lijn bg-kaart p-4 shadow-[0_2px_12px_rgba(35,38,28,0.06)]">
      <div className="mb-2 flex min-h-8 items-center justify-between">
        <h4 className="font-bold font-display text-sm capitalize">{titel}</h4>
        {nav && (
          <div className="-my-1.5 flex gap-1">
            <NavKnop
              richting="‹"
              label={nl.kalender.vorigeMaanden(titel)}
              uit={nav.vorigeUit}
              onClick={nav.vorige}
            />
            <NavKnop
              richting="›"
              label={nl.kalender.volgendeMaanden(titel)}
              uit={nav.volgendeUit}
              onClick={nav.volgende}
            />
          </div>
        )}
      </div>
      <div className="grid grid-cols-7 gap-[3px]">
        {DOW.map((day, index) => (
          <span
            key={day}
            className={`pb-1 text-center font-mono text-[10px] uppercase tracking-wider ${
              index >= 4 ? "font-semibold text-dennen" : "text-mist"
            }`}
          >
            {day}
          </span>
        ))}
        {cells.map((cell) => {
          const iso = cell.date.toString();
          // Padding days from a neighbouring month: an empty slot, not a
          // number — grey ghosts made the grid restless without adding
          // anything (design review, 2026-08-21).
          if (!cell.inMonth) {
            return <span key={iso} aria-hidden className="aspect-[1/0.82]" />;
          }
          if (cell.past) {
            // Decoration, not data: not bookable and never fetched, so it says
            // nothing a screen reader should announce.
            return (
              <span
                key={iso}
                aria-hidden
                className={`aspect-[1/0.82] rounded-md border p-1 text-left font-mono text-[10.5px] ${cellClass(cell, false)}`}
              >
                {cell.date.day}
              </span>
            );
          }
          const label = STATE_LABEL[cell.state];
          return (
            <button
              key={iso}
              type="button"
              onClick={() => onSelectDay(iso)}
              aria-label={`${cell.date.day}: ${label}`}
              className={`group relative aspect-[1/0.82] cursor-pointer rounded-md border p-1 text-left font-mono text-[10.5px] ${cellClass(cell, isSelected(iso))}`}
            >
              {cell.date.day}
              {/* Instant hover tooltip: the status, never the group (NFR-6). */}
              <span className="-top-7 -translate-x-1/2 pointer-events-none absolute left-1/2 z-10 hidden whitespace-nowrap rounded-md bg-inkt px-2 py-1 font-sans text-[11px] text-kaart group-hover:block group-focus-visible:block">
                {label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
